import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { connectState } from '@y42/angular/util';
import { MAT_DATE_WITH_TIME_FNS_FORMATS, Y42DateAdapter } from '@y42/date-time';
import { BehaviorSubject, tap } from 'rxjs';
import { DatepickerRange } from '../types';

export interface DateRangeFormControlModel {
  from: Date | null;
  to: Date | null;
}

@UntilDestroy()
@Component({
  selector: 'y42-daterange-picker-control',
  templateUrl: './daterange-picker-control.component.html',
  styleUrls: ['./daterange-picker-control.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: DateAdapter, useClass: Y42DateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: MAT_DATE_WITH_TIME_FNS_FORMATS },
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: DateRangePickerControlComponent,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: DateRangePickerControlComponent,
    },
  ],
})
export class DateRangePickerControlComponent implements ControlValueAccessor, Validator, OnInit {
  @Input() label = '';
  @Input() compact?: boolean | null;

  @Input()
  minDate: Date | null = null;

  @Input()
  maxDate: Date | null = null;

  onChange?: ((value: DateRangeFormControlModel | null) => void) | undefined;
  onTouched?: (() => void) | undefined;
  onValidatorChange?: (() => void) | undefined;

  readonly form = new FormGroup({
    from: new FormControl<DateRangeFormControlModel['from']>(null),
    to: new FormControl<DateRangeFormControlModel['to']>(null),
  });

  private readonly isMenuOpened$$ = new BehaviorSubject<boolean>(false);

  readonly state = connectState(this, {
    isMenuOpened: this.isMenuOpened$$,
  });

  ngOnInit() {
    this.form.valueChanges
      .pipe(
        tap(() => this.onChange?.(this.form.getRawValue())),
        untilDestroyed(this),
      )
      .subscribe();
  }

  validate(_control: FormControl<DateRangeFormControlModel>): ValidationErrors | null {
    if (this.form.invalid) {
      return this.form.errors || this.form.controls.from.errors || this.form.controls.to.errors;
    }

    return null;
  }

  writeValue(value: DateRangeFormControlModel): void {
    this.form.setValue(value, { emitEvent: false });
  }

  registerOnChange(fn: (value: DateRangeFormControlModel | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean) {
    if (disabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  protected pickerDatesChange({ start, end }: DatepickerRange) {
    // IMPORTANT: we need to prevent this callback when no menu opened
    if (!this.isMenuOpened$$.value) {
      return;
    }

    this.form.setValue({ from: start, to: end });
  }

  protected menuOpened(isOpened: boolean) {
    this.isMenuOpened$$.next(isOpened);
  }
}
